/**
 * ExerciseBpCard — iter1058 — как реагира налягането при сила срещу аеробно,
 * персонализирано по текущата степен, ортостатиката и застоя.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { AlertTriangle, ArrowRight, ChevronDown, Dumbbell, Loader2, Waves } from "lucide-react";
import { toast } from "sonner";

const CLEARANCE_TONE = {
  stop: { light: "bg-rose-50 border-rose-200", dark: "bg-rose-900/25 border-rose-500/40" },
  restricted: { light: "bg-orange-50 border-orange-200", dark: "bg-orange-900/25 border-orange-500/40" },
  careful: { light: "bg-amber-50 border-amber-200", dark: "bg-amber-900/20 border-amber-500/30" },
  low_bp: { light: "bg-sky-50 border-sky-200", dark: "bg-sky-900/20 border-sky-500/30" },
  green: { light: "bg-emerald-50 border-emerald-200", dark: "bg-emerald-900/20 border-emerald-500/30" },
  unknown: { light: "bg-slate-50 border-slate-200", dark: "bg-slate-800/40 border-slate-700" },
};

const RuleList = ({ rules, isDark, prefix }) => {
  const [open, setOpen] = useState(null);
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";
  return (
    <div className="space-y-1.5">
      {rules.map((r, i) => (
        <div key={i} className={`rounded-xl overflow-hidden ${box}`}>
          <button onClick={() => setOpen(open === i ? null : i)}
                  className="w-full text-left p-2.5 flex items-center gap-2"
                  data-testid={`${prefix}-rule-${i}`}>
            <span className="text-[11px] font-bold flex-1 min-w-0">{r.title_bg}</span>
            <ChevronDown className={`w-3 h-3 opacity-50 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && (
            <div className="px-2.5 pb-2.5 text-[11px] leading-relaxed opacity-85"
                 data-testid={`${prefix}-rule-text-${i}`}>{r.text_bg}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export const ExerciseBpCard = ({ isDark, refreshKey }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("aerobic");
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/cardio/exercise-bp`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API, refreshKey]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { modality: tab };
      ["pre_sbp", "pre_dbp", "peak_sbp", "peak_dbp", "peak_hr"].forEach((k) => {
        if (form[k]) payload[k] = parseFloat(form[k]);
      });
      const { data } = await axios.post(`${API}/cardio/exercise-bp`, payload);
      setData(data);
      setShowForm(false);
      setForm({});
      toast.success(data.verdict?.label_bg || "Записано.");
    } catch {
      toast.error("Записът не мина.");
    } finally {
      setSaving(false);
    }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";
  const inputCls = `px-2 py-1.5 rounded-md border text-sm w-full ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`;

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${card} flex justify-center`} data-testid="exercise-bp-loading">
      <Loader2 className="w-5 h-5 animate-spin text-red-500" /></div>;
  }
  if (!data) return null;
  const tone = CLEARANCE_TONE[data.clearance?.level] || CLEARANCE_TONE.unknown;
  const last = data.history?.[0];

  return (
    <div className={`p-5 rounded-2xl border ${card}`} data-testid="exercise-bp-card">
      <div className="flex items-center gap-2 mb-1">
        <Dumbbell className="w-4 h-4 text-red-500" />
        <h2 className="font-bold text-sm">Налягането при натоварване</h2>
      </div>
      <p className="text-[11px] opacity-80 leading-relaxed mb-2" data-testid="exercise-bp-summary">
        {data.summary_bg}
      </p>

      <div className={`p-3 rounded-xl mb-3 border ${isDark ? tone.dark : tone.light}`} data-testid="exercise-bp-clearance">
        <div className="text-xs font-bold" data-testid="exercise-bp-clearance-label">{data.clearance.label_bg}</div>
        {data.clearance.text_bg && (
          <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{data.clearance.text_bg}</div>
        )}
      </div>

      {data.personal?.length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid="exercise-bp-personal">
          {data.personal.map((p, i) => (
            <div key={i} className={`p-2.5 rounded-xl ${box}`} data-testid={`exercise-bp-personal-${i}`}>
              <div className="text-[11px] font-bold">{p.title_bg}</div>
              <div className="text-[11px] opacity-85 mt-0.5 leading-relaxed">{p.text_bg}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1.5 mb-2">
        {[["aerobic", "Аеробно", Waves], ["strength", "Силово", Dumbbell]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`exercise-bp-tab-${k}`}
                  className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1 ${
                    tab === k ? "bg-red-600 text-white" : box}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div data-testid={`exercise-bp-rules-${tab}`}>
        <RuleList rules={data[tab].rules} isDark={isDark} prefix={`exercise-bp-${tab}`} />
      </div>

      <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-rose-900/20 border border-rose-500/30" : "bg-rose-50 border border-rose-200"}`}
           data-testid="exercise-bp-red-flags">
        <div className="flex items-center gap-1.5 mb-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />
          <span className="text-xs font-bold">Кога отговорът е ненормален</span>
        </div>
        <RuleList rules={data.red_flags} isDark={isDark} prefix="exercise-bp-flag" />
      </div>

      {last?.verdict && (
        <div className={`mt-3 p-3 rounded-xl ${last.verdict.abnormal
          ? (isDark ? "bg-amber-900/20 border border-amber-500/30" : "bg-amber-50 border border-amber-200")
          : (isDark ? "bg-emerald-900/20 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200")}`}
             data-testid="exercise-bp-verdict">
          <div className="text-xs font-bold" data-testid="exercise-bp-verdict-label">{last.verdict.label_bg}</div>
          <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{last.verdict.text_bg}</div>
          {last.verdict.notes_bg?.map((n, i) => (
            <div key={i} className="text-[11px] opacity-80 mt-1 leading-relaxed">• {n}</div>
          ))}
        </div>
      )}

      {!showForm ? (
        <button onClick={() => setShowForm(true)} data-testid="exercise-bp-measure"
                className="w-full mt-3 py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold">
          Измерих налягане около тренировка
        </button>
      ) : (
        <div className="mt-3 space-y-2" data-testid="exercise-bp-form">
          <div className="grid grid-cols-2 gap-2">
            {[["pre_sbp", "Систолно преди"], ["pre_dbp", "Диастолно преди"],
              ["peak_sbp", "Систолно на върха"], ["peak_dbp", "Диастолно на върха"],
              ["peak_hr", "Пулс на върха"]].map(([k, label]) => (
              <label key={k} className="flex flex-col gap-1">
                <span className="text-[10px] opacity-70">{label}</span>
                <input type="number" className={inputCls} value={form[k] || ""}
                       data-testid={`exercise-bp-input-${k}`}
                       onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </label>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="exercise-bp-save"
                  className="w-full py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Записвам…" : `Оцени отговора (${tab === "strength" ? "силово" : "аеробно"})`}
          </button>
        </div>
      )}

      {data.links?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="exercise-bp-links">
          {data.links.map((l, i) => (
            <button key={i} onClick={() => navigate(l.route)} data-testid={`exercise-bp-link-${i}`}
                    className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
              {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExerciseBpCard;
